import { writeThirdPartyRegistrationPayload, takeThirdPartyLoginPayload } from '../utils/third-party-login'
import {
  getFirstLoginRedirectQueryValue,
  resolveDefaultLoginRedirectUrl,
  resolveLoginRedirectUrl,
} from '../utils/login-redirect'

const isFalseLike = (value: unknown) => value === false || value === 0 || String(value).toLowerCase() === 'false'

const getFirstQueryValue = (value: string | string[] | null | undefined) => getFirstLoginRedirectQueryValue(value)

/** 读取第三方回调 URL 中可能使用不同大小写格式传递的登录 Cookie。 */
const getCallbackCookieValue = (route: ReturnType<typeof useRoute>) =>
  getFirstQueryValue(route.query.cookieValue || route.query.cookievalue)

/** 读取外部三方回调中可能携带的目标页面地址。 */
const getCallbackReturnUrl = (route: ReturnType<typeof useRoute>) =>
  route.query.returnurl || route.query.from || route.query.fromurl || ''

type ThirdPartyCallbackResult = 'settled' | 'redirected' | 'none'

export const useThirdPartyCallback = () => {
  const route = useRoute()
  const appStore = useAppStore()
  const { accessCookie, setCookieFromThirdParty, setLoginTokenCookies } = useLogin()
  const { invokeV3 } = useLoginApi()

  /** 解析三方回调完成后的安全回跳地址。 */
  const getPostLoginRedirectUrl = () =>
    resolveLoginRedirectUrl(
      getCallbackReturnUrl(route),
      resolveDefaultLoginRedirectUrl(appStore.LanguageCode),
      window.location.origin,
    )

  /** 仅回到同源页面，确保 sessionStorage 中的第三方身份可以被登录弹窗读取。 */
  const getRegistrationRedirectUrl = () => {
    const registrationUrl = new URL(getPostLoginRedirectUrl(), window.location.origin)
    if (registrationUrl.origin === window.location.origin) {
      return registrationUrl.href
    }

    return resolveDefaultLoginRedirectUrl(appStore.LanguageCode)
  }

  /** 将外部回调返回的未注册身份暂存后回到发起授权的页面。 */
  const redirectToRegistration = (payload: Record<string, any>): ThirdPartyCallbackResult => {
    const registrationPayload = {
      id: String(payload.id || payload.openId || payload.OpenId || payload.userId || ''),
      accountType: String(
        payload.logintype || payload.loginType || payload.accountType || payload.LoginType || payload.AccountType || '',
      ),
      email: String(payload.email || payload.Email || ''),
      token: String(payload.token || payload.Token || ''),
    }
    writeThirdPartyRegistrationPayload(registrationPayload)
    window.location.href = getRegistrationRedirectUrl()
    return 'redirected'
  }

  /** 外部回调没有登录凭据时，判断是否应进入第三方注册流程。 */
  const isRegistrationPayload = (payload: Record<string, any>) => {
    const registerValue = payload.isRegister ?? payload.IsRegister ?? payload.isregister
    return (
      isFalseLike(registerValue) ||
      Boolean(payload.id || payload.openId || payload.OpenId || payload.email || payload.Email)
    )
  }

  /** 处理 sessionStorage 中暂存的 Google、Apple 或 Telegram 登录 payload。 */
  const processStoredPayload = async (): Promise<ThirdPartyCallbackResult> => {
    if (String(route.query.thirdpayload || '') !== '1') return 'none'

    const payload = takeThirdPartyLoginPayload()
    if (!payload) return 'none'

    if (!payload.cookieValue && !payload.userId && !payload.LoginToken && isRegistrationPayload(payload)) {
      return redirectToRegistration(payload)
    }

    if (payload.userId || payload.LoginToken) {
      return (await accessCookie(payload)) ? 'settled' : 'none'
    }

    if (payload.cookieValue) {
      await setCookieFromThirdParty(String(payload.cookieValue), {
        userId: payload.userId || '',
        nick: payload.nickName || payload.nick || '',
        avatar: payload.img || payload.avatar || '',
      })
      return 'settled'
    }

    return 'none'
  }

  /** 处理外部回调直接携带的 cookie 或一次性 code。 */
  const processQueryPayload = async (): Promise<ThirdPartyCallbackResult> => {
    let settled = false
    const tokenCode = String(route.query.code || '')
    if (tokenCode) {
      const result = await invokeV3('usercenter$gettokenbycode', { code: tokenCode }).catch(() => null)
      const token = result?.usercenter?.gettokenbycode?.result || result?.usercenter?.gettokenbycode?.data
      if (token) {
        setLoginTokenCookies(token)
        settled = true
      }
    }

    const rawCookieValue = getCallbackCookieValue(route)
    if (rawCookieValue) {
      const cookieValue = decodeURIComponent(rawCookieValue)
      await setCookieFromThirdParty(cookieValue)
      settled = true
    }

    return settled ? 'settled' : 'none'
  }

  /** 将 query 形式的第三方身份转换为当前页绑定邮箱所需的参数。 */
  const getQueryRegistrationPayload = () => ({
    id: getFirstQueryValue(route.query.id || route.query.openId || route.query.openid || route.query.userId),
    name: getFirstQueryValue(route.query.name || route.query.nickName || route.query.nick),
    img: getFirstQueryValue(route.query.img || route.query.avatar),
    email: getFirstQueryValue(route.query.email || route.query.Email),
    loginType: getFirstQueryValue(route.query.logintype || route.query.loginType || route.query.accountType),
    provider: getFirstQueryValue(route.query.provider),
  })

  /** 处理外部三方回调并在登录成功后回到授权前页面。 */
  const processThirdPartyCallback = async () => {
    const storedResult = await processStoredPayload()
    const result = storedResult === 'none' ? await processQueryPayload() : storedResult
    if (result === 'none') {
      const queryPayload = getQueryRegistrationPayload()
      if (String(route.query.type || '') === 'register' || isRegistrationPayload(queryPayload)) {
        redirectToRegistration(queryPayload)
        return true
      }
      return false
    }

    if (result === 'redirected') return true

    if (!appStore.userlogin2 && import.meta.client) {
      window.setTimeout(() => {
        window.location.href = getPostLoginRedirectUrl()
      }, 1200)
    }

    return true
  }

  return {
    getPostLoginRedirectUrl,
    processThirdPartyCallback,
  }
}
